import {useState} from 'react';
import pick from 'lodash/pick';
import moment from 'moment-timezone';
import {connect} from 'react-redux';

import Button from '@mui/material/Button';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';

import { 
  setDate, 
  setTime, 
  setSessionTypeId, 
  setProviderAvailabilityId,
  setProviderId,
  setServiceType,
  setLocationType,
  setLocationId,
  setTimeCreated,
  setScheduleManually,
  // setError,
  reset,
} from '../../redux/reducers/newAppointmentForm';

import CustomBox from '../common/CustomBox';
import PatientWaysToBook from './PatientWaysToBook';
import PatientSchedulingLocationForm from './PatientSchedulingLocationForm';
import PatientSchedulingServiceForm from './PatientSchedulingServiceForm';
import PatientSchedulingProviderForm from './PatientSchedulingProviderForm';
import PatientSchedulingLocationTypeForm from './PatientSchedulingLocationTypeForm';

const getSteps = ({
  prev, 
  next,
  setLocation,
  setService,
  setSearchMethod,
  setProviderId,
  setProviderAvailabilityId,
  saveDate,
  saveTime,
  saveSessionTypeId,
  saveProviderAvailabilityId,
  saveProviderId,
  saveServiceType,
  saveLocationType,
  saveLocationId,
  saveTimeCreated,
  saveScheduleManually,
  afterSubmit,
  reset,
}) => [
  {
    key: 'searchMethod',
    name: 'searchMethod',
    layout: 'row',
    searchMethod: null,
    Component: PatientWaysToBook,
    onSubmit: (e, val) => {
      setSearchMethod(val);
      next();
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingLocationForm,
    searchMethod: null,
    onSubmit: (e, address) => {
      if (address) {
        setLocation(address);
      } else {
        setLocation(e?.target?.value);
      }
      next();
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingServiceForm,
    onSubmit: (e, serviceCat) => {
      setService(serviceCat);
      saveServiceType(serviceCat);
      next();
    },
  },
  {
    padding: '0em',
    showPreviousProviders: true,
    Component: PatientSchedulingProviderForm,
    onSubmit: (e, {
      isoString,
      providerId,
      providerAvailabilityId,
      sessionTypeId,
      scheduleManually,
      timezone,
    }) => {
      // The form stores its state in redux automatically
      setProviderId(providerId);
      if (scheduleManually) {
        saveScheduleManually(true);
        saveDate(null);
        saveTime(null);
        saveSessionTypeId(null);
        saveProviderAvailabilityId(null);
        setProviderAvailabilityId(null);
        saveProviderId(providerId);
        if (afterSubmit) afterSubmit({providerId});
      } else {
        const providerTimezone = timezone || moment.tz.guess();
        const dateStr = moment.tz(isoString, providerTimezone).format('MM/DD/yyyy');
        const timeStr = moment.tz(isoString, providerTimezone).format('hh:mm a');
        saveDate(dateStr);
        saveTime(timeStr);
        saveProviderAvailabilityId(providerAvailabilityId);
        setProviderAvailabilityId(providerAvailabilityId);
        saveProviderId(providerId);
        saveSessionTypeId(sessionTypeId);
        saveScheduleManually(false);
        next();
      }
    },
  },
  {
    padding: '2em',
    Component: PatientSchedulingLocationTypeForm,
    onSubmit: (e, formData) => {
      console.log('PatientSchedulingLocationTypeForm.onSubmit', e, formData);
      const {location_type, location_id} = formData;
      saveLocationType(location_type);
      saveLocationId(location_id);
      saveTimeCreated(moment().toISOString());
      if (afterSubmit) afterSubmit(e, formData);
    },
  },
];

const filterStepsForMethod = searchMethod => step => (
  step?.searchMethod 
  ? step.searchMethod === searchMethod
  : true
);

const ExistingPatientBookingForm = props => {
  const [stepIndex, setStepIndex] = useState(
    props?.stepIndex ? props.stepIndex 
    : props?.searchMethod ? 1
    : 0
  );
  const [location, setLocation] = useState(props?.location || null);
  const [serviceCategory, setService] = useState(props?.serviceCategory || null);
  const [providerId, setProviderId] = useState(props?.providerId || null);
  const [providerAvailabilityId, setProviderAvailabilityId] = useState(props?.providerAvailabilityId || null);
  const [timeCreated, setTimeCreated] = useState(props?.timeCreated || null);
  const [searchMethod, setSearchMethod] = useState(props?.searchMethod || null);
  const steps = getSteps({
    next: () => setStepIndex(Math.min(steps.length - 1, stepIndex + 1)),
    prev: () => setStepIndex(Math.max(0, stepIndex - 1)),
    setLocation,
    setService,
    setSearchMethod,
    setProviderId,
    setProviderAvailabilityId,
    saveDate: props.saveDate,
    saveTime: props.saveTime,
    saveSessionTypeId: props.saveSessionTypeId,
    saveProviderAvailabilityId: props.saveProviderAvailabilityId,
    saveProviderId: props.saveProviderId,
    saveServiceType: props.saveServiceType,
    saveLocationType: props.saveLocationType,
    saveLocationId: props.saveLocationId,
    saveTimeCreated: props.saveTimeCreated,
    saveScheduleManually: props.saveScheduleManually,
    afterSubmit: props.afterSubmit,
  })
    .filter(filterStepsForMethod(searchMethod));
  const step = steps[stepIndex || 0];
  const childProps = {
    ...pick(step, ['key', 'name', 'layout', 'onSubmit', 'showPreviousProviders']),
    location, 
    serviceCategory,
    searchMethod,
    providerAvailabilityId,
    onChangeSearchMethod: (e, val) => setSearchMethod(val),
  };
  return (
    <Container maxWidth='xs' align='center' mt={2}>
      {stepIndex > 0 && 
        <Stack direction='row' align='flex-start'>
          <Button
            onClick={() => setStepIndex(stepIndex - 1)}
            startIcon={<ChevronLeftIcon />}
          >
            Back
          </Button>
        </Stack>
      }
      <CustomBox p={2}>
        <step.Component {...childProps} />
      </CustomBox>
    </Container>
  );
};

ExistingPatientBookingForm.defaultProps = {
  stepIndex: 0,
};

const mapState = state => ({
  stepIndex: state.booking?.stepIndex,
  searchMethod: state.booking?.searchMethod,
});

const mapDispatch = dispatch => ({
  saveDate: dateStr => dispatch(setDate(dateStr)),
  saveTime: timeStr => dispatch(setTime(timeStr)),
  saveSessionTypeId: sessionTypeId => dispatch(setSessionTypeId(sessionTypeId)),
  saveProviderAvailabilityId: providerAvailabilityId => 
    dispatch(setProviderAvailabilityId(providerAvailabilityId)),
  saveProviderId: providerId => dispatch(setProviderId(providerId)),
  saveServiceType: serviceType => dispatch(setServiceType(serviceType)),
  saveLocationType: locationType => dispatch(setLocationType(locationType)),
  saveLocationId: locationId => dispatch(setLocationId(locationId)),
  saveTimeCreated: timeCreated => dispatch(setTimeCreated(timeCreated)),
  saveScheduleManually: scheduleManually => dispatch(setScheduleManually(scheduleManually)),
  reset: () => dispatch(reset()),
});


export default connect(mapState, mapDispatch)(ExistingPatientBookingForm);